(() => {
  if (!window.customElements || window.customElements.get('about-pattern-wall')) return;

  const pointerOptions = { passive: true };

  class AboutPatternWall extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._inViewport = false;
      this._activeTile = null;
      this._observer = null;
      this._frame = 0;
      this._bounds = null;
      this._point = null;
      this._lastTime = 0;
      this._updateMotion = this._updateMotion.bind(this);
      this._onPointerEnter = this._onPointerEnter.bind(this);
      this._onPointerLeave = this._onPointerLeave.bind(this);
      this._onPointerOver = this._onPointerOver.bind(this);
      this._onPointerOut = this._onPointerOut.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._invalidateBounds = this._invalidateBounds.bind(this);
      this._renderLight = this._renderLight.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
      this._fineQuery = window.matchMedia?.('(any-hover: hover) and (any-pointer: fine)') || null;
      this._light = this.querySelector('.about-studio__cursor-light');

      this.addEventListener('pointerenter', this._onPointerEnter, pointerOptions);
      this.addEventListener('pointerleave', this._onPointerLeave, pointerOptions);
      this.addEventListener('pointercancel', this._onPointerLeave, pointerOptions);
      this.addEventListener('pointerover', this._onPointerOver, pointerOptions);
      this.addEventListener('pointerout', this._onPointerOut, pointerOptions);
      this.addEventListener('pointermove', this._onPointerMove, pointerOptions);
      window.addEventListener('resize', this._invalidateBounds, pointerOptions);
      window.addEventListener('scroll', this._invalidateBounds, pointerOptions);
      if (window.ResizeObserver) {
        this._resizeObserver = new window.ResizeObserver(this._invalidateBounds);
        this._resizeObserver.observe(this);
      }
      document.addEventListener('visibilitychange', this._updateMotion);
      for (const query of [this._motionQuery, this._fineQuery]) {
        if (query?.addEventListener) query.addEventListener('change', this._updateMotion);
        else query?.addListener(this._updateMotion);
      }

      this._inViewport = !window.IntersectionObserver;
      if (window.IntersectionObserver) {
        const observer = new window.IntersectionObserver((entries) => {
          if (!this._connected || this._observer !== observer) return;
          for (const entry of entries) {
            if (entry.target === this) this._inViewport = entry.isIntersecting;
          }
          this._updateMotion();
        });
        this._observer = observer;
        observer.observe(this);
      }
      this._resetInteraction();
      this._updateMotion();
    }

    disconnectedCallback() {
      this._connected = false;
      this._observer?.disconnect();
      this._observer = null;
      this._resizeObserver?.disconnect();
      this._resizeObserver = null;
      this.removeEventListener('pointerenter', this._onPointerEnter, pointerOptions);
      this.removeEventListener('pointerleave', this._onPointerLeave, pointerOptions);
      this.removeEventListener('pointercancel', this._onPointerLeave, pointerOptions);
      this.removeEventListener('pointerover', this._onPointerOver, pointerOptions);
      this.removeEventListener('pointerout', this._onPointerOut, pointerOptions);
      this.removeEventListener('pointermove', this._onPointerMove, pointerOptions);
      window.removeEventListener('resize', this._invalidateBounds, pointerOptions);
      window.removeEventListener('scroll', this._invalidateBounds, pointerOptions);
      document.removeEventListener('visibilitychange', this._updateMotion);
      for (const query of [this._motionQuery, this._fineQuery]) {
        if (query?.removeEventListener) query.removeEventListener('change', this._updateMotion);
        else query?.removeListener(this._updateMotion);
      }
      this._resetInteraction();
      this.removeAttribute('data-running');
      this._motionQuery = this._fineQuery = null;
      this._light = null;
    }

    _motionAllowed() {
      return this._connected && this._inViewport && !document.hidden && !this._motionQuery?.matches;
    }

    _canInteract() {
      return this._motionAllowed() && Boolean(this._fineQuery?.matches);
    }

    _isFinePointer(event) {
      return event.pointerType === 'mouse' || event.pointerType === 'pen';
    }

    _updateMotion() {
      if (!this._connected) return;
      this.toggleAttribute('data-running', this._motionAllowed());
      if (!this._canInteract()) this._resetInteraction();
    }

    _onPointerEnter(event) {
      if (!this._canInteract() || !this._isFinePointer(event)) return;
      this.setAttribute('data-interacting', '');
      this._bounds = null;
      this._point = null;
      this._onPointerMove(event);
    }

    _onPointerLeave(event) {
      if (event.pointerType && !this._isFinePointer(event)) return;
      this._resetInteraction();
    }

    _onPointerMove(event) {
      if (!this._light || !this._canInteract() || !this._isFinePointer(event) || !this.hasAttribute('data-interacting')) return;
      // Pointer events only store the newest position. One RAF owns all visual writes.
      this._clientX = event.clientX;
      this._clientY = event.clientY;
      this._scheduleLight();
    }

    _invalidateBounds() {
      this._bounds = null;
      if (this.hasAttribute('data-interacting')) this._scheduleLight();
    }

    _scheduleLight() {
      if (!this._frame && this._light && this._canInteract()) {
        this._frame = window.requestAnimationFrame(this._renderLight);
      }
    }

    _renderLight(time) {
      this._frame = 0;
      if (!this._canInteract() || !this.hasAttribute('data-interacting')) return;
      // Read geometry only on entry, resize or scroll; never for each pointer event.
      this._bounds ||= this.getBoundingClientRect();
      const x = this._clientX - this._bounds.left;
      const y = this._clientY - this._bounds.top;
      const elapsed = this._lastTime ? Math.min(time - this._lastTime, 48) : 16;
      const blend = 1 - Math.exp(-elapsed / 65);
      this._lastTime = time;
      if (!this._point) this._point = { x, y };
      this._point.x += (x - this._point.x) * blend;
      this._point.y += (y - this._point.y) * blend;
      this._light.style.setProperty('--light-x', `${this._point.x.toFixed(2)}px`);
      this._light.style.setProperty('--light-y', `${this._point.y.toFixed(2)}px`);
      if (Math.abs(x - this._point.x) + Math.abs(y - this._point.y) > .15) this._scheduleLight();
      else this._lastTime = 0;
    }

    _tileFrom(target) {
      const tile = target?.closest?.('.about-studio__tile');
      return tile && this.contains(tile) ? tile : null;
    }

    _onPointerOver(event) {
      if (this._canInteract() && this._isFinePointer(event)) {
        this._setActiveTile(this._tileFrom(event.target));
      }
    }

    _onPointerOut(event) {
      if (this._canInteract() && this._isFinePointer(event)) {
        this._setActiveTile(this._tileFrom(event.relatedTarget));
      }
    }

    _setActiveTile(tile) {
      if (tile === this._activeTile) return;
      this._activeTile?.removeAttribute('data-active');
      this._activeTile = tile;
      this._activeTile?.setAttribute('data-active', '');
    }

    _resetInteraction() {
      if (this._frame) window.cancelAnimationFrame(this._frame);
      this._frame = 0;
      this._point = null;
      this._bounds = null;
      this._lastTime = 0;
      this.removeAttribute('data-interacting');
      this._setActiveTile(null);
    }
  }

  window.customElements.define('about-pattern-wall', AboutPatternWall);
})();
